import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Upload, Video, Trash2, Plus, X, Save, Eye, Lock, ArrowRight, BarChart3, Users, TrendingUp, Calendar } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminPanel = ({ onClose }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  
  // Video form
  const [showVideoForm, setShowVideoForm] = useState(false);
  const [videoForm, setVideoForm] = useState({
    title: '',
    title_ar: '',
    description: '',
    vimeo_url: '',
    youtube_url: '',
    category: 'nursing',
    course_slug: '',
    order: 0
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/verify`, { password });
      setIsAuthenticated(true);
      loadVideos();
      loadAnalytics();
    } catch (error) {
      const msg = error?.response?.data?.detail || 'סיסמה שגויה';
      setMessage({ type: 'error', text: typeof msg === 'string' ? msg : 'סיסמה שגויה' });
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics/summary`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    }
  };

  const loadVideos = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/videos`);
      setVideos(response.data);
    } catch (error) {
      console.error('Failed to load videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await axios.post(`${API}/admin/videos`, videoForm);
      setMessage({ type: 'success', text: 'הסרטון נוסף בהצלחה!' });
      setShowVideoForm(false);
      setVideoForm({
        title: '',
        title_ar: '',
        description: '',
        vimeo_url: '',
        youtube_url: '',
        category: 'nursing',
        course_slug: '',
        order: 0
      });
      loadVideos();
    } catch (error) {
      setMessage({ type: 'error', text: 'שגיאה בהוספת הסרטון' });
    } finally {
      setLoading(false);
    }
  };

  const deleteVideo = async (videoId) => {
    if (!window.confirm('האם למחוק את הסרטון?')) return;
    try {
      await axios.delete(`${API}/admin/videos/${videoId}`);
      setMessage({ type: 'success', text: 'הסרטון נמחק' });
      loadVideos();
    } catch (error) {
      setMessage({ type: 'error', text: 'שגיאה במחיקת הסרטון' });
    }
  };

  const extractVimeoId = (url) => {
    const match = url.match(/vimeo\.com\/(\d+)/);
    return match ? match[1] : null;
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" dir="rtl">
        <div className="bg-slate-900 rounded-2xl p-8 max-w-md w-full border border-slate-700">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <Lock className="w-6 h-6 text-teal-500" />
              פאנל ניהול
            </h2>
            <button onClick={onClose} className="text-slate-400 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-slate-400 mb-2">סיסמת מנהל</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-slate-800 border border-slate-600 rounded-xl px-4 py-3 text-white focus:border-teal-500 focus:outline-none"
                placeholder="הכנס סיסמה"
              />
            </div>
            
            {message && (
              <div className={`p-3 rounded-lg ${message.type === 'error' ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>
                {message.text}
              </div>
            )}
            
            <button
              type="submit"
              className="w-full bg-teal-500 hover:bg-teal-400 text-white font-bold py-3 rounded-xl transition-colors"
            >
              כניסה
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 overflow-auto" dir="rtl">
      <div className="min-h-screen p-4 sm:p-8">
        {/* Header */}
        <div className="max-w-6xl mx-auto mb-8">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-black text-white flex items-center gap-3">
              <div className="w-12 h-12 bg-teal-500 rounded-xl flex items-center justify-center">
                <Video className="w-6 h-6" />
              </div>
              פאנל ניהול
            </h1>
            <button
              onClick={onClose}
              className="bg-slate-800 hover:bg-slate-700 text-white px-4 py-2 rounded-xl flex items-center gap-2"
            >
              <ArrowRight className="w-5 h-5" />
              חזרה לאתר
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-6xl mx-auto mb-6">
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => { setActiveTab('dashboard'); loadAnalytics(); }}
              className={`px-6 py-3 rounded-xl font-bold transition-colors flex items-center gap-2 ${
                activeTab === 'dashboard' 
                  ? 'bg-teal-500 text-white' 
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              <BarChart3 className="w-5 h-5" />
              לוח בקרה
            </button>
            <button
              onClick={() => setActiveTab('videos')}
              className={`px-6 py-3 rounded-xl font-bold transition-colors flex items-center gap-2 ${
                activeTab === 'videos' 
                  ? 'bg-teal-500 text-white' 
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              <Video className="w-5 h-5" />
              סרטונים
            </button>
          </div>
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-6 text-white">
                <div className="flex items-center justify-between mb-4">
                  <TrendingUp className="w-8 h-8 opacity-80" />
                  <span className="text-sm opacity-80">סה"כ</span>
                </div>
                <div className="text-3xl font-black">{analytics?.total_visits?.toLocaleString() || 0}</div>
                <div className="text-sm opacity-80">ביקורים</div>
              </div>
              
              <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-2xl p-6 text-white">
                <div className="flex items-center justify-between mb-4">
                  <Calendar className="w-8 h-8 opacity-80" />
                  <span className="text-sm opacity-80">היום</span>
                </div>
                <div className="text-3xl font-black">{analytics?.today_visits?.toLocaleString() || 0}</div>
                <div className="text-sm opacity-80">ביקורים</div>
              </div>
              
              <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-2xl p-6 text-white">
                <div className="flex items-center justify-between mb-4">
                  <Users className="w-8 h-8 opacity-80" />
                  <span className="text-sm opacity-80">ייחודיים</span>
                </div>
                <div className="text-3xl font-black">{analytics?.unique_visitors?.toLocaleString() || 0}</div>
                <div className="text-sm opacity-80">מבקרים</div>
              </div>
              
              <div className="bg-gradient-to-br from-amber-600 to-amber-700 rounded-2xl p-6 text-white">
                <div className="flex items-center justify-between mb-4">
                  <Users className="w-8 h-8 opacity-80" />
                  <span className="text-sm opacity-80">רשומים</span>
                </div>
                <div className="text-3xl font-black">{analytics?.total_users?.toLocaleString() || 0}</div>
                <div className="text-sm opacity-80">משתמשים</div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
              <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700">
                <h3 className="text-lg font-bold text-white mb-4">📊 סטטיסטיקות תקופתיות</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">ביקורים השבוע</span>
                    <span className="text-xl font-bold text-white">{analytics?.week_visits?.toLocaleString() || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">ביקורים החודש</span>
                    <span className="text-xl font-bold text-white">{analytics?.month_visits?.toLocaleString() || 0}</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700">
                <h3 className="text-lg font-bold text-white mb-4">🔥 דפים פופולריים</h3>
                <div className="space-y-2">
                  {analytics?.popular_pages?.length > 0 ? (
                    analytics.popular_pages.slice(0, 5).map((page, i) => (
                      <div key={i} className="flex justify-between items-center text-sm">
                        <span className="text-slate-400">{page._id || 'ראשי'}</span>
                        <span className="text-teal-400 font-bold">{page.count}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-slate-500">אין נתונים עדיין</p>
                  )}
                </div>
              </div>
            </div>
            
            <div className="text-center">
              <button
                onClick={loadAnalytics}
                className="bg-slate-800 hover:bg-slate-700 text-white px-6 py-3 rounded-xl font-bold transition-colors"
              >
                🔄 רענן נתונים
              </button>
            </div>
          </div>
        )}

        {/* Message */}
        {message && activeTab === 'videos' && (
          <div className="max-w-6xl mx-auto mb-4">
            <div className={`p-4 rounded-xl ${message.type === 'error' ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>
              {message.text}
              <button onClick={() => setMessage(null)} className="float-left">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="max-w-6xl mx-auto">
          {activeTab === 'videos' && (
            <div className="space-y-6">
              {/* Add Video Button */}
              <button
                onClick={() => setShowVideoForm(true)}
                className="bg-teal-500 hover:bg-teal-400 text-white px-6 py-3 rounded-xl font-bold flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                הוסף סרטון
              </button>

              {/* Video Form Modal */}
              {showVideoForm && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
                  <div className="bg-slate-800 rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-auto">
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-xl font-bold text-white">הוספת סרטון חדש</h3>
                      <button onClick={() => setShowVideoForm(false)} className="text-slate-400 hover:text-white">
                        <X className="w-6 h-6" />
                      </button>
                    </div>

                    <form onSubmit={handleVideoSubmit} className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-slate-400 mb-2">כותרת (עברית)</label>
                          <input
                            type="text"
                            value={videoForm.title}
                            onChange={(e) => setVideoForm({...videoForm, title: e.target.value})}
                            className="w-full bg-slate-700 border border-slate-600 rounded-xl px-4 py-3 text-white"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-slate-400 mb-2">כותרת (ערבית)</label>
                          <input
                            type="text"
                            value={videoForm.title_ar}
                            onChange={(e) => setVideoForm({...videoForm, title_ar: e.target.value})}
                            className="w-full bg-slate-700 border border-slate-600 rounded-xl px-4 py-3 text-white"
                            dir="rtl"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-slate-400 mb-2">תיאור</label>
                        <textarea
                          value={videoForm.description}
                          onChange={(e) => setVideoForm({...videoForm, description: e.target.value})}
                          className="w-full bg-slate-700 border border-slate-600 rounded-xl px-4 py-3 text-white h-24"
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-slate-400 mb-2">קישור Vimeo</label>
                          <input
                            type="url"
                            value={videoForm.vimeo_url}
                            onChange={(e) => setVideoForm({...videoForm, vimeo_url: e.target.value})}
                            className="w-full bg-slate-700 border border-slate-600 rounded-xl px-4 py-3 text-white"
                            placeholder="https://vimeo.com/123456789"
                          />
                        </div>
                        <div>
                          <label className="block text-slate-400 mb-2">קישור YouTube</label>
                          <input
                            type="url"
                            value={videoForm.youtube_url}
                            onChange={(e) => setVideoForm({...videoForm, youtube_url: e.target.value})}
                            className="w-full bg-slate-700 border border-slate-600 rounded-xl px-4 py-3 text-white"
                            placeholder="https://youtube.com/watch?v=..."
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-slate-400 mb-2">קטגוריה</label>
                          <select
                            value={videoForm.category}
                            onChange={(e) => setVideoForm({...videoForm, category: e.target.value})}
                            className="w-full bg-slate-700 border border-slate-600 rounded-xl px-4 py-3 text-white"
                          >
                            <option value="nursing">סיעוד</option>
                            <option value="medical">רפואה</option>
                            <option value="dental">רפואת שיניים</option>
                            <option value="pharmacy">רוקחות</option>
                            <option value="education">חינוך</option>
                            <option value="law">משפטים</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-slate-400 mb-2">סדר הצגה</label>
                          <input
                            type="number"
                            value={videoForm.order}
                            onChange={(e) => setVideoForm({...videoForm, order: parseInt(e.target.value)})}
                            className="w-full bg-slate-700 border border-slate-600 rounded-xl px-4 py-3 text-white"
                          />
                        </div>
                      </div>

                      <div className="flex gap-3 pt-4">
                        <button
                          type="submit"
                          disabled={loading}
                          className="flex-1 bg-teal-500 hover:bg-teal-400 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2"
                        >
                          <Save className="w-5 h-5" />
                          {loading ? 'שומר...' : 'שמור'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowVideoForm(false)}
                          className="px-6 bg-slate-600 hover:bg-slate-500 text-white font-bold py-3 rounded-xl"
                        >
                          ביטול
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* Videos List */}
              <div className="bg-slate-800 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-white mb-4">סרטונים ({videos.length})</h3>
                
                {loading ? (
                  <div className="text-center py-8">
                    <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                  </div>
                ) : videos.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <Video className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>אין סרטונים עדיין</p>
                    <p className="text-sm">לחץ "הוסף סרטון" כדי להתחיל</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {videos.map((video) => (
                      <div key={video.id} className="bg-slate-700 rounded-xl p-4 flex items-center gap-4">
                        {/* Thumbnail */}
                        <div className="w-32 h-20 bg-slate-600 rounded-lg flex items-center justify-center flex-shrink-0">
                          {video.vimeo_url ? (
                            <img 
                              src={`https://vumbnail.com/${extractVimeoId(video.vimeo_url)}.jpg`}
                              alt={video.title}
                              className="w-full h-full object-cover rounded-lg"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          ) : (
                            <Video className="w-8 h-8 text-slate-400" />
                          )}
                        </div>
                        
                        {/* Info */}
                        <div className="flex-1">
                          <h4 className="font-bold text-white">{video.title}</h4>
                          {video.title_ar && (
                            <p className="text-slate-400 text-sm">{video.title_ar}</p>
                          )}
                          <div className="flex gap-2 mt-1">
                            <span className="bg-teal-500/20 text-teal-400 text-xs px-2 py-1 rounded">
                              {video.category}
                            </span>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2">
                          {video.vimeo_url && (
                            <a
                              href={video.vimeo_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 bg-slate-600 hover:bg-slate-500 rounded-lg text-white"
                            >
                              <Eye className="w-5 h-5" />
                            </a>
                          )}
                          <button
                            onClick={() => deleteVideo(video.id)}
                            className="p-2 bg-red-500/20 hover:bg-red-500/40 rounded-lg text-red-400"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
